import {
    Box,
    Button,
    Grid,
    Typography,
    TextField,
    Stack,
    Paper,
    Autocomplete,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import axios from 'axios';

import fallbackImage from '../assets/icons/fallbackImage.png';

import VehicleExploreSection from './VehicleExploreSection';
import DealerListSection from './DealerListSection';
import BannerSection from "./BannerSection"; // adjust path if different
import axiosInstance from '../services/axiosInstance';
import { enqueueSnackbar } from 'notistack';
import HeroSection from './HeroSection';


const HomeShowcase = () => {
    const navigate = useNavigate();
    const [vehicles, setVehicles] = useState<any[]>([]);
    const [dealerData, setDealerData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [lightCommercialList, setLightCommercialList] = useState<any[]>([]);
    const [heavyCommercialList, setHeavyCommercialList] = useState<any[]>([]);
    const [adSpaceList, setAdSpaceList] = useState<any[]>([]);
    const [adSpaceBanner, setAdSpaceBanner] = useState<any[]>([]);




    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await axiosInstance.post(
                    "/CustomerVehicle/VehicleMultipleDataList",
                    {
                        vehicleBodyTypeData: true,
                        vehicleTransmissionTypeData: true,
                        vehicleClassData: true,
                        vehicleBrandData: true,
                        vehicleModelData: true,
                        dealerCompanyData: true,
                        adSpaceData: true,
                    },
                    { headers: { "Content-Type": "application/json" } }
                );

                const result = response.data as {
                    responseCode: string;
                    responseMessage?: string;
                    responseData?: {
                        vehicleBodyTypeData?: any[];
                        vehicleTransmissionTypeData?: any[];
                        vehicleClassData?: any[];
                        vehicleBrandData?: any[];
                        vehicleModelData?: any[];
                        dealerCompanyData?: any[];
                        adSpaceData?: any[];
                    };
                };

                if (result.responseCode === "00" && result.responseData) {
                    setDealerData(result.responseData.dealerCompanyData || []);
                    setAdSpaceList(result.responseData.adSpaceData || []);

                    // 🔹 filter only homepage banner(s)
                    const homePageBanner = (result.responseData.adSpaceData || []).filter(
                        (ad: any) => ad.adPositionCode === "homePage-Banner"
                    );
                    setAdSpaceBanner(homePageBanner);
                } else {
                    enqueueSnackbar(result.responseMessage || "⚠️ Failed to load data", {
                        variant: "error",
                    });
                }
            } catch {
                enqueueSnackbar("❌ Error fetching vehicle metadata", { variant: "error" });
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    useEffect(() => {
        const fetchVehicles = async () => {
            setLoading(true);
            try {
                const response = await axiosInstance.post(
                    "/CustomerVehicle/VehicleGetListPublished",
                    {},
                    { headers: { "Content-Type": "application/json" } }
                );

                const result = response.data as {
                    responseCode: string;
                    responseMessage?: string;
                    responseData?: { vehicleList?: any[] };
                };

                if (result.responseCode === "00" && result.responseData) {
                    const vehicleList = result.responseData.vehicleList || [];

                    const lightCommercial = vehicleList.filter((v) => v.vehicleClassId === 2);
                    const heavyCommercial = vehicleList.filter((v) => v.vehicleClassId === 3);

                    setVehicles(vehicleList);
                    setLightCommercialList(lightCommercial);
                    setHeavyCommercialList(heavyCommercial);

                    // console.log("All Vehicles:", vehicleList);
                    // console.log("Light Commercial:", lightCommercial);
                    // console.log("Heavy Commercial:", heavyCommercial);
                } else {
                    enqueueSnackbar(result.responseMessage || "⚠️ Failed to load vehicles", {
                        variant: "error",
                    });
                }
            } catch {
                enqueueSnackbar("❌ Error fetching vehicles", { variant: "error" });
            } finally {
                setLoading(false);
            }
        };

        fetchVehicles();
    }, []);




    return (
        <>
            {/* Banner */}
            <BannerSection adSpaceBanner={adSpaceBanner} />

            {/* Chart */}
            <Box
                sx={{
                    width: '100%',
                    maxWidth: 1200,
                    mx: 'auto',
                    // 🚀 RESPONSIVE MARGIN: 0 on mobile, -10 on desktop (md and up)
                    mt: { xs: -5, md: -10 },

                    position: 'relative',
                    zIndex: 10,
                }}
            >
                <Typography
                    variant="h6"
                    color="text.secondary"
                    sx={{
                        maxWidth: 700,
                        mx: 'auto',
                        lineHeight: 1.6,
                        display: 'none' // 👈 This hides the component
                    }}
                >
                    WorkHorse is Singapore’s commercial vehicle platform for vans, lorries & trucks.
                    Browse listings, compare options & connect with trusted dealers.
                </Typography>

                <HeroSection />
            </Box>

            {/* Filters */}
            <Box sx={{ width: '100%', maxWidth: 1100, mx: 'auto', pb: 4 }}>

                {/* Other sections */}

                {/* Only show if vehicles array has items */}
                {vehicles?.length > 0 && (
                    <VehicleExploreSection
                        title="Explore Our Vehicles"
                        navigateTo="/vehicle-listing"
                        vehicle={vehicles}
                    />
                )}

                {/* Only show if dealerData array has items */}
                {dealerData?.length > 0 && (
                    <DealerListSection
                        dealerData={dealerData}
                        adSpaceList={adSpaceList}
                    />
                )}

                {/* Only show if lightCommercialList has items */}
                {lightCommercialList?.length > 0 && (
                    <VehicleExploreSection
                        title="Explore Light Vehicles Commercial"
                        navigateTo="/vehicle-listing?vehicleClassIds=2"
                        vehicle={lightCommercialList}
                    />
                )}

                {/* Only show if heavyCommercialList has items */}
                {heavyCommercialList?.length > 0 && (
                    <VehicleExploreSection
                        title="Explore Heavy Vehicles Commercial"
                        navigateTo="/vehicle-listing?vehicleClassIds=3"
                        vehicle={heavyCommercialList}
                    />
                )}

            </Box>


        </>
    );
};

export default HomeShowcase;
